class KlevuInstantSearchPopup extends HHBaseElement {
  _open = false
  constructor() {
    super()
    this.show = this.show.bind(this)
    this.hide = this.hide.bind(this)
    this.trigger = null
    this._handleDocumentClick = this._handleDocumentClick.bind(this)
    this._handleDocumentKeydown = this._handleDocumentKeydown.bind(this)
    this._handleQueryChange = this._handleQueryChange.bind(this)
    this.closeBtn = this.querySelector('[data-instant-search-close]')
    this.$results = this.querySelector('.klevu-results')
    this.$input = this.querySelector('#ku-search-field')
    this.$submitBtns = Array.from(
      this.querySelectorAll('.klevu-instant-search__submit')
    )
    this.clearBtn = this.querySelector('[data-instant-search-clear]')
    this._handleClear = this._handleClear.bind(this)
    this._handleMutation = this._handleMutation.bind(this)
  }

  get open() {
    return this._open
  }

  set open(value) {
    this._open = value
    this.classList.toggle('active', value)
    if (!this._open) {
      this._handleClose()
    } else {
      this._handleOpen()
    }

    if (this._open) {
      setTimeout(() => {
        this.$input?.focus()
      }, 100)
    }
  }

  get $backdrop() {
    return document.querySelector('main-menu .js-backdrop')
  }

  get $headerBar() {
    return document.querySelector('main-menu .js-main-header')
  }

  reset() {
    klevu.search.quick.getScope().element.value = ''
  }

  connectedCallback() {
    this._attachListeners()
    this._setupFocusTrap()
  }

  disconnectedCallback() {
    this._removeListeners()
  }

  _attachListeners() {
    this.addEventListener('instant-search-input::change', this._handleQueryChange)
    document.addEventListener('instant-search-popup::open', this.show)
    document.addEventListener('click', this._handleDocumentClick)
    document.addEventListener('keydown', this._handleDocumentKeydown)
    this.$input?.addEventListener('keyup', this._handleQueryChange)
    this.$input?.addEventListener('change', this._handleQueryChange)
    this.closeBtn.addEventListener('click', this.hide)
    this.clearBtn?.addEventListener('click', this._handleClear)
    this.observer = new MutationObserver(this._handleMutation)

    this.observer.observe(this.$results, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
      subtree: true,
    })
  }

  // component-product-card.js removes itself when its ?view= fetch 404s (an
  // unavailable product still in the Klevu index), leaving an empty slide that
  // Swiper still counts and can show first.
  _pruneEmptySlides() {
    const empty = Array.from(
      this.$results.querySelectorAll('swiper-slide')
    ).filter((slide) => !slide.querySelector('product-card'))

    if (!empty.length) return

    const containers = new Set()
    empty.forEach((slide) => {
      const container = slide.closest('swiper-container')
      if (container) containers.add(container)
      slide.remove()
    })
    containers.forEach((container) => container.swiper?.update())
  }

  _handleQueryChange(event) {
    const query = event.detail?.value?.trim()
    const hasQuery = !!event.currentTarget.value?.trim() || !!query
    this.classList.toggle('has-query', hasQuery)
    this.$submitBtns.forEach((submitBtn) =>
      hasQuery
        ? submitBtn.removeAttribute('disabled')
        : submitBtn.setAttribute('disabled', true)
    )
  }

  _resetQueryState() {
    if (this.$input) this.$input.value = ''
    this.classList.remove('has-query')
    this.$submitBtns.forEach((submitBtn) =>
      submitBtn.setAttribute('disabled', true)
    )
    this.reset()
  }

  _handleClear() {
    this._resetQueryState()
    this.$input?.focus()
  }

  _handleDocumentKeydown(event) {
    if (event.key === 'Escape' && this.open) {
      event.preventDefault()
      this.open = false
    }
  }

  scrollToElementTop(elem, offset) {
    offset = offset || 0

    const rect = elem.getBoundingClientRect()
    const padding = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        '--header-spacer'
      )
    )
    const targetPosition = rect.top + window.pageYOffset + offset - padding

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth',
    })

    return new Promise((resolve) => {
      const scrollHandler = () => {
        if (window.pageYOffset === targetPosition) {
          window.removeEventListener('scroll', scrollHandler)
          resolve()
        }
      }
      window.addEventListener('scroll', scrollHandler)
    })
  }

  async _handleDocumentClick(event) {
    if (event.target.hasAttribute('data-instant-search-toggle')) {
      if (event.target.hasAttribute('data-search-results-page')) {
        await this.scrollToElementTop(
          document.querySelector('klevu-landing .klevu-landing__form')
        )
        document.querySelector('klevu-landing .kuSearchInput').focus()
      } else {
        this.trigger = event.target
        this.open ? this.hide() : this.show()
      }
    }
  }

  _removeListeners() {
    this.removeEventListener('instant-search-input::change', this._handleQueryChange)
    document.removeEventListener('instant-search-popup::open', this.show)
    document.removeEventListener('click', this._handleDocumentClick)
    document.removeEventListener('keydown', this._handleDocumentKeydown)
    this.closeBtn.removeEventListener('click', this.hide)
    this.clearBtn?.removeEventListener('click', this._handleClear)
    this.$input?.removeEventListener('keyup', this._handleQueryChange)
    this.$input?.removeEventListener('change', this._handleQueryChange)
    this.observer?.disconnect()
    clearTimeout(this._pruneTimer)
  }

  _handleMutation() {
    this.$results.removeAttribute('style')

    // Debounced: card hydration rewrites each card's innerHTML, so this fires often.
    clearTimeout(this._pruneTimer)
    this._pruneTimer = setTimeout(() => this._pruneEmptySlides(), 150)
  }

  show() {
    this.open = true
  }

  hide() {
    this.open = false
  }

  _handleOpen() {
    Array.from(
      document.querySelectorAll('[data-instant-search-toggle]')
    ).forEach((el) => {
      el.classList.add('is-open')
      el.setAttribute('aria-expanded', 'true')
      if (el.dataset.closeLabel) {
        el.setAttribute('aria-label', el.dataset.closeLabel)
      }
    })
    this.$backdrop?.classList.add('visible')
    this.$headerBar?.classList.add('is-scrolled')
    this._freezeBody()
  }

  _handleClose() {
    setTimeout(() => {
      this._resetQueryState()
    }, 150)

    Array.from(
      document.querySelectorAll('[data-instant-search-toggle]')
    ).forEach((el) => {
      el.classList.remove('is-open')
      el.setAttribute('aria-expanded', 'false')
      if (el.dataset.searchLabel) {
        el.setAttribute('aria-label', el.dataset.searchLabel)
      }
    })
    this.$backdrop?.classList.remove('visible')
    this.$headerBar?.classList.toggle('is-scrolled', window.scrollY > 40)

    if (this.trigger){
      this.trigger.focus()
    }

    this._unfreezeBody()
  }
}

window.customElements.define(
  'klevu-instant-search-popup',
  KlevuInstantSearchPopup
)
